/**
 * A/R Collected — Finance & Mgmt.
 * Created 2026-05-06 per Chris Georghiou request.
 *
 * Cash Received drilldown for any month Sage retains. Default: current month.
 * Sub-tabs by Salesman / PM / Client / Job / Receipts. All columns sortable
 * (house rule). Available either via the sidebar or by clicking "Collected
 * This Month" on the A/R Report page.
 *
 * 2026-05-22 (Chris Georghiou): added Quarter / YTD / Fiscal Year period modes
 * in addition to Month. ACS FY = Oct 1 → Sep 30 (Q1=Oct–Dec, Q2=Jan–Mar,
 * Q3=Apr–Jun, Q4=Jul–Sep). All views use the existing /ar/collections endpoint
 * via from/to date params — no backend change.
 *
 * Filter rule (Pat Kross 2026-04-24): Deposit_Date in window only, no
 * Transaction_Type filter, SUM(-Amount) nets refunds correctly.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Grid, Chip, Tabs, Tab,
  TextField, FormControl, InputLabel, Select, MenuItem, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Alert, CircularProgress, IconButton, Tooltip, ButtonGroup, InputAdornment,
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon, Download as DownloadIcon,
  Refresh as RefreshIcon, Search as SearchIcon, Clear as ClearIcon,
} from '@mui/icons-material';
import { useLocation } from 'react-router-dom';
import { getArCollections, getArCollectionsMonths } from '../services/api';
import { useSortableTable } from '../utils/useSortableTable';

const fmt$ = (n) => (n === null || n === undefined || n === '') ? '—'
  : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(n));
const fmtDate = (d) => !d ? '—' : new Date(d).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
const fmtMonthLabel = (ym) => {
  if (!ym) return '—';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

const SummaryCard = ({ label, value, sub, color }) => (
  <Paper elevation={2} sx={{ p: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="h6" fontWeight="bold" color={color}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
  </Paper>
);

function thisMonthYM() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function lastMonthYM() {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// ─── ACS Fiscal Calendar ─────────────────────────────────────────────────
// FY = Oct 1 → Sep 30. FY26 = Oct 1, 2025 → Sep 30, 2026.
// Q1=Oct–Dec, Q2=Jan–Mar, Q3=Apr–Jun, Q4=Jul–Sep.
function fiscalPeriodOf(date = new Date()) {
  const m = date.getMonth() + 1; // 1..12
  const cy = date.getFullYear();
  const fy = m >= 10 ? cy + 1 : cy;
  const fMonth = m >= 10 ? m - 9 : m + 3; // 1..12 in fiscal order
  const q = Math.ceil(fMonth / 3);
  return { fy, q };
}
const pad = (n) => String(n).padStart(2, '0');
function ymd(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function lastDay(y, m) { return new Date(y, m, 0).getDate(); }

// Quarter → first/last calendar dates.
function fyQuarterRange(fy, q) {
  let startY, startM, endY, endM;
  if (q === 1) { startY = fy - 1; startM = 10; endY = fy - 1; endM = 12; }
  else if (q === 2) { startY = fy; startM = 1;  endY = fy; endM = 3; }
  else if (q === 3) { startY = fy; startM = 4;  endY = fy; endM = 6; }
  else             { startY = fy; startM = 7;  endY = fy; endM = 9; }
  return { from: ymd(startY, startM, 1), to: ymd(endY, endM, lastDay(endY, endM)) };
}
function fyRange(fy) {
  return { from: ymd(fy - 1, 10, 1), to: ymd(fy, 9, 30) };
}
function fyYtdRange(today = new Date()) {
  const { fy } = fiscalPeriodOf(today);
  return {
    from: ymd(fy - 1, 10, 1),
    to:   ymd(today.getFullYear(), today.getMonth() + 1, today.getDate()),
    fy,
  };
}
function fyLabel(fy) { return `FY${String(fy).slice(-2)}`; }
function quarterLabel(fy, q) {
  const names = { 1: 'Q1 (Oct–Dec)', 2: 'Q2 (Jan–Mar)', 3: 'Q3 (Apr–Jun)', 4: 'Q4 (Jul–Sep)' };
  return `${fyLabel(fy)} ${names[q]}`;
}
function fmtRangeLabel(from, to) {
  const f = new Date(from + 'T00:00:00');
  const t = new Date(to   + 'T00:00:00');
  const opts = { month: 'short', day: 'numeric', year: 'numeric' };
  return `${f.toLocaleDateString('en-US', opts)} → ${t.toLocaleDateString('en-US', opts)}`;
}

export default function ArCollected() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const initialMonth = params.get('month') || thisMonthYM();
  const initialMode  = params.get('mode')  || 'month'; // month | quarter | ytd | fy

  // Period selection state
  const today = useMemo(() => new Date(), []);
  const fpNow = useMemo(() => fiscalPeriodOf(today), [today]);

  const [mode, setMode]   = useState(initialMode);
  const [month, setMonth] = useState(initialMonth);
  const [fyQ, setFyQ]     = useState({ fy: fpNow.fy, q: fpNow.q });
  const [fyYr, setFyYr]   = useState(fpNow.fy);

  const [availableMonths, setAvailableMonths] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState(0);

  // Compute the active {from,to,label} for whichever mode is selected.
  const period = useMemo(() => {
    if (mode === 'quarter') {
      const { from, to } = fyQuarterRange(fyQ.fy, fyQ.q);
      return { from, to, label: quarterLabel(fyQ.fy, fyQ.q) };
    }
    if (mode === 'ytd') {
      const { from, to, fy } = fyYtdRange(today);
      return { from, to, label: `${fyLabel(fy)} YTD` };
    }
    if (mode === 'fy') {
      const { from, to } = fyRange(fyYr);
      return { from, to, label: fyLabel(fyYr) };
    }
    const [y, m] = month.split('-').map(Number);
    return {
      from: ymd(y, m, 1),
      to:   ymd(y, m, lastDay(y, m)),
      label: fmtMonthLabel(month),
    };
  }, [mode, month, fyQ, fyYr, today]);

  const load = async (p = period) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getArCollections({ from: p.from, to: p.to });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await getArCollectionsMonths();
        setAvailableMonths(r.data?.months || []);
      } catch (_) { /* non-fatal */ }
    })();
    load(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload whenever the active window changes.
  useEffect(() => {
    load(period);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.from, period.to]);

  const filteredRows = useMemo(() => {
    if (!data?.rows) return [];
    if (!search) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter(r =>
      [r.client_name, r.ar_code, r.invoice, r.job, r.job_name, r.salesman, r.project_manager, r.receipt_ref]
        .filter(Boolean).some(v => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  const receiptsTable  = useSortableTable(filteredRows,             { defaultKey: 'deposit_date', defaultDir: 'desc' });
  const salesmanTable  = useSortableTable(data?.by_salesman || [],  { defaultKey: 'total', defaultDir: 'desc' });
  const pmTable        = useSortableTable(data?.by_pm || [],        { defaultKey: 'total', defaultDir: 'desc' });
  const clientTable    = useSortableTable(data?.by_client || [],    { defaultKey: 'total', defaultDir: 'desc' });
  const jobTable       = useSortableTable(data?.by_job || [],       { defaultKey: 'total', defaultDir: 'desc' });

  const goMonth = (m) => { setMode('month'); setMonth(m); };

  // Last 3 FYs (current + 2 prior) for FY / Quarter pickers.
  const fyOptions = useMemo(() => {
    const list = [];
    for (let i = 0; i < 3; i++) list.push(fpNow.fy - i);
    return list;
  }, [fpNow]);

  const exportCsv = () => {
    if (!filteredRows.length) return;
    const header = ['DepositDate','TxnDate','ARCode','Client','Invoice','Job','JobName','Salesman','PM','ReceiptRef','AmountPaid'];
    const rows = receiptsTable.sortedRows.map(r => [
      r.deposit_date && new Date(r.deposit_date).toISOString().slice(0,10),
      r.transaction_date && new Date(r.transaction_date).toISOString().slice(0,10),
      r.ar_code, r.client_name, r.invoice, r.job, r.job_name,
      r.salesman, r.project_manager, r.receipt_ref, r.amount_paid,
    ]);
    const csv = [header, ...rows].map(r => r.map(v => {
      if (v == null) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const safeLabel = String(period.label).replace(/[^A-Za-z0-9_-]+/g, '-');
    a.download = `ar-collected-${safeLabel}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalanceIcon color="primary" />
          <Typography variant="h5" fontWeight="bold">A/R Collected</Typography>
          {data && <Chip label={period.label} size="small" color="primary" variant="outlined" />}
          {data && <Chip label={`${data.row_count} receipts`} size="small" variant="outlined" />}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh"><IconButton onClick={() => load(period)}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv} disabled={!filteredRows.length}>
            Export CSV
          </Button>
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={2}>
        Cash receipts in the selected period, joined to Sage Job for Salesman / PM. Deposit_Date drives the filter — no Transaction_Type guards. Refunds net against receipts. Fiscal year runs Oct 1 → Sep 30.
      </Typography>

      {/* Period picker — Month / Quarter / YTD / Fiscal Year */}
      <Paper elevation={1} sx={{ p: 1.5, mb: 2 }}>
        <Tabs
          value={mode}
          onChange={(_, v) => setMode(v)}
          sx={{ mb: 1.5, minHeight: 36 }}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="month"   label="Month"        sx={{ minHeight: 36, py: 0 }} />
          <Tab value="quarter" label="Quarter"      sx={{ minHeight: 36, py: 0 }} />
          <Tab value="ytd"     label="YTD"          sx={{ minHeight: 36, py: 0 }} />
          <Tab value="fy"      label="Fiscal Year"  sx={{ minHeight: 36, py: 0 }} />
        </Tabs>

        <Grid container spacing={1.5} alignItems="center">
          {mode === 'month' && (
            <>
              <Grid item xs={12} md="auto">
                <ButtonGroup size="small">
                  <Button variant={month === thisMonthYM() ? 'contained' : 'outlined'} onClick={() => goMonth(thisMonthYM())}>This Month</Button>
                  <Button variant={month === lastMonthYM() ? 'contained' : 'outlined'} onClick={() => goMonth(lastMonthYM())}>Last Month</Button>
                </ButtonGroup>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Pick a month</InputLabel>
                  <Select label="Pick a month" value={month} onChange={e => goMonth(e.target.value)}>
                    {availableMonths.length === 0 && (
                      <MenuItem value={month}>{fmtMonthLabel(month)}</MenuItem>
                    )}
                    {availableMonths.map(m => (
                      <MenuItem key={m.month} value={m.month}>
                        {fmtMonthLabel(m.month)} — {fmt$(m.total)} ({m.txn_count} txn)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}

          {mode === 'quarter' && (
            <>
              <Grid item xs={6} sm={4} md={2}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Fiscal Year</InputLabel>
                  <Select label="Fiscal Year" value={fyQ.fy} onChange={e => setFyQ({ ...fyQ, fy: Number(e.target.value) })}>
                    {fyOptions.map(fy => (
                      <MenuItem key={fy} value={fy}>{fyLabel(fy)} ({fy - 1}-10 → {fy}-09)</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md="auto">
                <ButtonGroup size="small">
                  {[1, 2, 3, 4].map(q => (
                    <Button
                      key={q}
                      variant={fyQ.q === q ? 'contained' : 'outlined'}
                      onClick={() => setFyQ({ ...fyQ, q })}
                    >
                      {quarterLabel(fyQ.fy, q).split(' ').slice(1).join(' ')}
                    </Button>
                  ))}
                </ButtonGroup>
              </Grid>
            </>
          )}

          {mode === 'ytd' && (
            <Grid item xs={12} md="auto">
              <Chip
                label={`${fyLabel(fpNow.fy)} YTD — Oct 1, ${fpNow.fy - 1} → today`}
                color="primary"
                variant="outlined"
              />
            </Grid>
          )}

          {mode === 'fy' && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Fiscal Year</InputLabel>
                <Select label="Fiscal Year" value={fyYr} onChange={e => setFyYr(Number(e.target.value))}>
                  {fyOptions.map(fy => (
                    <MenuItem key={fy} value={fy}>{fyLabel(fy)} ({fy - 1}-10 → {fy}-09)</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md="auto" sx={{ ml: { md: 'auto' } }}>
            <Chip
              size="small"
              variant="outlined"
              label={fmtRangeLabel(period.from, period.to)}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small" fullWidth label="Search receipts"
              value={search} onChange={e => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: search && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Summary */}
      {data && (
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Total Collected" value={fmt$(data.total)} color="success.main" sub={`${data.window?.from} → ${data.window?.to}`} />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Receipts" value={data.row_count} />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Top Salesperson" value={data.by_salesman?.[0]?.key || '—'} sub={fmt$(data.by_salesman?.[0]?.total)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <SummaryCard label="Top Client" value={data.by_client?.[0]?.key || '—'} sub={fmt$(data.by_client?.[0]?.total)} />
          </Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {!loading && data && (
        <Paper elevation={1}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
            <Tab label={`By Salesman (${data.by_salesman?.length || 0})`} />
            <Tab label={`By Client (${data.by_client?.length || 0})`} />
            <Tab label={`By Job (${data.by_job?.length || 0})`} />
            <Tab label={`By PM (${data.by_pm?.length || 0})`} />
            <Tab label={`Receipts (${filteredRows.length})`} />
          </Tabs>

          {tab === 0 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {salesmanTable.sortableCell('key',          'Salesperson')}
                  {salesmanTable.sortableCell('txn_count',    'Receipts', 'right')}
                  {salesmanTable.sortableCell('client_count', 'Clients', 'right')}
                  {salesmanTable.sortableCell('total',        'Total $', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {salesmanTable.sortedRows.map(g => (
                    <TableRow key={g.key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{g.key}</TableCell>
                      <TableCell align="right">{g.txn_count}</TableCell>
                      <TableCell align="right">{g.client_count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt$(g.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 1 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {clientTable.sortableCell('key',       'Client')}
                  {clientTable.sortableCell('txn_count', 'Receipts', 'right')}
                  {clientTable.sortableCell('total',     'Total $', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {clientTable.sortedRows.map(g => (
                    <TableRow key={g.key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{g.key}</TableCell>
                      <TableCell align="right">{g.txn_count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt$(g.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 2 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {jobTable.sortableCell('key',       'Job#')}
                  {jobTable.sortableCell('txn_count', 'Receipts', 'right')}
                  {jobTable.sortableCell('total',     'Total $', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {jobTable.sortedRows.map(g => (
                    <TableRow key={g.key} hover>
                      <TableCell><Chip label={g.key || '—'} size="small" variant="outlined" /></TableCell>
                      <TableCell align="right">{g.txn_count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt$(g.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 3 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {pmTable.sortableCell('key',          'PM')}
                  {pmTable.sortableCell('txn_count',    'Receipts', 'right')}
                  {pmTable.sortableCell('client_count', 'Clients', 'right')}
                  {pmTable.sortableCell('total',        'Total $', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {pmTable.sortedRows.map(g => (
                    <TableRow key={g.key} hover>
                      <TableCell sx={{ fontWeight: 600 }}>{g.key}</TableCell>
                      <TableCell align="right">{g.txn_count}</TableCell>
                      <TableCell align="right">{g.client_count}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt$(g.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {tab === 4 && (
            <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)' }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow>
                  {receiptsTable.sortableCell('deposit_date',     'Deposit Date')}
                  {receiptsTable.sortableCell('client_name',      'Client')}
                  {receiptsTable.sortableCell('ar_code',          'AR Code')}
                  {receiptsTable.sortableCell('invoice',          'Invoice')}
                  {receiptsTable.sortableCell('job',              'Job#')}
                  {receiptsTable.sortableCell('salesman',         'Salesman')}
                  {receiptsTable.sortableCell('project_manager',  'PM')}
                  {receiptsTable.sortableCell('receipt_ref',      'Receipt Ref')}
                  {receiptsTable.sortableCell('amount_paid',      'Amount', 'right')}
                </TableRow></TableHead>
                <TableBody>
                  {receiptsTable.sortedRows.map((r, i) => (
                    <TableRow key={`${r.invoice}-${r.deposit_date}-${i}`} hover>
                      <TableCell>{fmtDate(r.deposit_date)}</TableCell>
                      <TableCell sx={{ maxWidth: 240, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.client_name || '—'}</TableCell>
                      <TableCell><Chip label={r.ar_code || '—'} size="small" variant="outlined" /></TableCell>
                      <TableCell>{r.invoice || '—'}</TableCell>
                      <TableCell>{r.job || '—'}</TableCell>
                      <TableCell>{r.salesman || '—'}</TableCell>
                      <TableCell>{r.project_manager || '—'}</TableCell>
                      <TableCell>{r.receipt_ref || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt$(r.amount_paid)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}
    </Box>
  );
}
